import { useEffect, useRef, useState } from 'react';
import { createSimulation } from '../../api/simulation';
import { useAuth } from '../../auth/AuthContext';
import { logEvent } from '../../utils/analytics';

// ── Asset combobox ────────────────────────────────────────────────────────────
function AssetCombobox({ value, onChange, onSelect, assetList, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const q = (value || '').toLowerCase();
  const matches = assetList.filter(
    (a) => a.name.toLowerCase().includes(q) || a.assetId.toLowerCase().includes(q)
  );

  return (
    <div ref={ref} className="relative">
      <input
        className={inputCls}
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-50 left-0 top-full mt-1 w-64 rounded-lg border border-white/15 bg-[#0d1421] shadow-xl overflow-hidden">
          {matches.map((a) => (
            <button
              key={a._id}
              type="button"
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-white/5 transition"
              onClick={() => { onSelect(a); setOpen(false); }}
            >
              <span className="shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] font-bold bg-white/10 text-slate-300">
                {a.assetId}
              </span>
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold text-white">{a.name}</p>
                <p className="text-[10px] text-slate-500">
                  {a.data.length}×{a.data.width}×{a.data.height} m
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Initial form state ────────────────────────────────────────────────────────
const INITIAL_FORM = {
  palletization: false,
  samePackageStackingOnly: false,
  containers: [
    { name: '20 ft Standard', length: 5.9, width: 2.35, height: 2.39, weight: 28310, maxCapacity: { weight: 100, volume: 100 }, cost: 2000, refrigerated: false },
    { name: '40 ft Standard', length: 12.03, width: 2.35, height: 2.39, weight: 26730, maxCapacity: { weight: 100, volume: 100 }, cost: 3000, refrigerated: false },
    { name: '20 ft High Cube', length: 5.9, width: 2.35, height: 2.7, weight: 28080, maxCapacity: { weight: 100, volume: 100 }, cost: 2500, refrigerated: false },
    { name: '40 ft High Cube', length: 12.03, width: 2.35, height: 2.7, weight: 28560, maxCapacity: { weight: 100, volume: 100 }, cost: 3500, refrigerated: false },
  ],
  pallets: [
    { name: 'Wooden Pallet', length: 1.2, width: 1, height: 0.145, weight: 30, maxWeightAllowed: 1500, sameItemOnly: true, isDoubleStackingAllowed: false },
  ],
  packages: [
    { name: 'Package 1', length: 0.232, width: 0.22, height: 0.25, maxStackingWeight: 20, maxStackingCount: 10, orientationRestricted: false, refrigerated: false, quantity: 1000 },
    { name: 'Package 2', length: 0.48, width: 0.216, height: 0.2, maxStackingWeight: 20, maxStackingCount: 10, orientationRestricted: false, refrigerated: false, quantity: 700 },
  ],
};

// ── Transform form → API payload ──────────────────────────────────────────────
function buildPayload(form) {
  return {
    handlingType: form.palletization ? 'palletized' : 'hand_stuffed',
    measurementUnits: { distance: 'meter', weight: 'kilogram', currency: 'usd', utilisation: 'percent' },
    containers: form.containers.reduce((acc, c) => {
      acc[c.name] = { length: c.length, width: c.width, height: c.height, weight: { value: c.weight, maxUtilisation: c.maxCapacity.weight }, maxVolumeUtilisation: c.maxCapacity.volume, cost: c.cost };
      return acc;
    }, {}),
    ...(form.palletization && {
      pallets: (form.pallets || []).reduce((acc, p) => {
        acc[p.name] = { length: p.length, width: p.width, height: p.height, weight: p.weight, maxWeightAllowed: p.maxWeightAllowed, sameItemOnly: p.sameItemOnly, isDoubleStackingAllowed: p.isDoubleStackingAllowed };
        return acc;
      }, {}),
    }),
    items: form.packages.reduce((acc, pkg) => {
      acc[pkg.name] = {
        length: pkg.length, width: pkg.width, height: pkg.height, weight: pkg.maxStackingWeight,
        quantity: pkg.quantity, sameStackingOnly: form.samePackageStackingOnly,
        maxStackingLevel: pkg.maxStackingCount,
        possibleOrientations: pkg.orientationRestricted
          ? [{ length: 'length', width: 'width', height: 'height' }]
          : [{ length: 'length', width: 'width', height: 'height' }, { length: 'width', width: 'length', height: 'height' }],
      };
      return acc;
    }, {}),
  };
}

// ── Parse API response → form state ──────────────────────────────────────────
export function responseToForm(data) {
  const containers = Object.entries(data.entities.containers).map(([name, c]) => ({
    name, length: c.length, width: c.width, height: c.height, weight: c.weight.value,
    maxCapacity: { weight: c.weight.maxUtilisation, volume: c.volume.maxUtilisation },
    cost: c.cost, refrigerated: false,
  }));
  const pallets = data.entities.pallets
    ? Object.entries(data.entities.pallets).map(([name, p]) => ({
        name, length: p.length, width: p.width, height: p.height, weight: p.weight,
        maxWeightAllowed: p.maxWeightAllowed, sameItemOnly: p.sameItemOnly,
        isDoubleStackingAllowed: p.isDoubleStackingAllowed,
      }))
    : undefined;
  const packages = Object.entries(data.entities.items).map(([name, item]) => ({
    name, length: item.length, width: item.width, height: item.height,
    maxStackingWeight: item.weight, maxStackingCount: item.maxStackingLevel,
    orientationRestricted: item.possibleOrientations.length === 1,
    refrigerated: false, quantity: item.quantity,
  }));
  return { palletization: data.entities.handlingType === 'palletized', samePackageStackingOnly: false, containers, pallets, packages };
}

// ── Shared input / toggle styles ──────────────────────────────────────────────
// Exported so other pages (e.g. BeyondCbmPage) can reuse the exact same
// console visual language instead of duplicating/drifting from it.
export const inputCls = 'w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-sm text-white placeholder:text-slate-500 outline-none transition focus:border-primary/60 focus:bg-white/10';
export const btnAdd = 'flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-bold text-slate-300 hover:bg-white/10 hover:text-white transition';
export const btnDel = 'h-7 w-7 shrink-0 rounded-full border border-white/10 bg-danger/10 text-danger flex items-center justify-center hover:bg-danger/20 transition';
export const thCls = 'pb-2 pr-4 text-left text-xs font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap';

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${value ? 'bg-primary' : 'bg-white/20'}`}
    >
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${value ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

// ── Main SimulationForm component ─────────────────────────────────────────────
// initialData: pre-fill form (used when forking an existing simulation)
const SimulationForm = ({ onResults, initialData, assetHook }) => {
  const [form, setForm] = useState(initialData || INITIAL_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, updateUser } = useAuth();

  const assets = assetHook?.assets || { item: [], container: [], pallet: [] };

  const setField = (section, index, field, value) => {
    setForm((prev) => {
      const arr = [...(prev[section])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [section]: arr };
    });
  };

  const setNestedField = (section, index, field, nested, value) => {
    setForm((prev) => {
      const arr = [...(prev[section])];
      arr[index] = { ...arr[index], [field]: { ...arr[index][field], [nested]: value } };
      return { ...prev, [section]: arr };
    });
  };

  const removeRow = (section, index) => {
    setForm((prev) => {
      const arr = [...prev[section]];
      arr.splice(index, 1);
      return { ...prev, [section]: arr };
    });
  };

  const handleRun = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await createSimulation(buildPayload(form), user?.token, user?.id);
      // createSimulation's response carries fresh quota (plan,
      // freeSimCreditsUsed) whenever this run was "expanded" — patch it into
      // AuthContext so the quota indicator updates immediately, instead of
      // showing stale numbers until the next login/refresh.
      if (data.quota) updateUser(data.quota);
      onResults(data, form);
      logEvent({
        name: 'simulation_run',
        type: 'simulation',
        userEmail: user?.email || '',
        meta: {
          items:      form.packages.length,
          containers: form.containers.length,
          palletized: form.palletization,
          strategies: Object.keys(data.strategies || {}).length,
        },
      });

      // Auto-save new assets (best-effort, non-blocking)
      if (assetHook?.saveFromSimulation) {
        const toSave = [
          ...form.packages.map((p) => ({
            type: 'item', name: p.name,
            data: { length: p.length, width: p.width, height: p.height, maxStackingWeight: p.maxStackingWeight, maxStackingCount: p.maxStackingCount, orientationRestricted: p.orientationRestricted, refrigerated: p.refrigerated },
          })),
          ...form.containers.map((c) => ({
            type: 'container', name: c.name,
            data: { length: c.length, width: c.width, height: c.height, weight: c.weight, maxCapacity: c.maxCapacity, cost: c.cost, refrigerated: c.refrigerated },
          })),
          ...(form.palletization ? (form.pallets || []).map((p) => ({
            type: 'pallet', name: p.name,
            data: { length: p.length, width: p.width, height: p.height, weight: p.weight, maxWeightAllowed: p.maxWeightAllowed, sameItemOnly: p.sameItemOnly, isDoubleStackingAllowed: p.isDoubleStackingAllowed },
          })) : []),
        ];
        assetHook.saveFromSimulation(toSave);
      }
    } catch (err) {
      setError(err.message);
      logEvent({
        name: 'simulation_error',
        type: 'simulation',
        userEmail: user?.email || '',
        meta: { error: err.message, items: form.packages.length, containers: form.containers.length },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="space-y-6">
      {/* Global toggles */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
        <h2 className="text-2xl font-black tracking-tight text-white">Simulation Configuration</h2>

        <div className="mt-6 flex flex-wrap gap-8">
          <div className="flex items-center gap-3">
            <Toggle value={form.palletization} onChange={(v) => setForm((f) => ({ ...f, palletization: v }))} />
            <span className="text-sm font-semibold text-slate-300">Use Palletization</span>
          </div>
          <div className="flex items-center gap-3">
            <Toggle value={form.samePackageStackingOnly} onChange={(v) => setForm((f) => ({ ...f, samePackageStackingOnly: v }))} />
            <span className="text-sm font-semibold text-slate-300">Same Package Stacking Only</span>
          </div>
        </div>
      </div>

      {/* Containers */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-lg font-bold text-white">Container Types</h3>
          <button className={btnAdd} onClick={() => setForm((f) => ({ ...f, containers: [...f.containers, { name: `Container ${f.containers.length + 1}`, length: 0, width: 0, height: 0, weight: 0, maxCapacity: { weight: 100, volume: 100 }, cost: 0, refrigerated: false }] }))}>
            <span className="material-symbols-outlined text-base">add</span> Add Container
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full">
            <thead><tr>
              {['Name', 'Length (m)', 'Width (m)', 'Height (m)', 'Weight (kg)', 'Max Wt %', 'Max Vol %', 'Cost (USD)', 'Refrig.', ''].map((h) => <th key={h} className={thCls}>{h}</th>)}
            </tr></thead>
            <tbody>
              {form.containers.map((c, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-2 pr-3">
                    <AssetCombobox
                      value={c.name}
                      assetList={assets.container}
                      placeholder="Container name"
                      onChange={(v) => setField('containers', i, 'name', v)}
                      onSelect={(a) => setForm((prev) => {
                        const arr = [...prev.containers];
                        arr[i] = { ...arr[i], name: a.name, ...a.data };
                        return { ...prev, containers: arr };
                      })}
                    />
                  </td>
                  {['length', 'width', 'height', 'weight'].map((f) => (
                    <td key={f} className="py-2 pr-3 w-20"><input className={inputCls} type="number" value={c[f]} onChange={(e) => setField('containers', i, f, parseFloat(e.target.value) || 0)} /></td>
                  ))}
                  <td className="py-2 pr-3 w-20"><input className={inputCls} type="number" value={c.maxCapacity.weight} onChange={(e) => setNestedField('containers', i, 'maxCapacity', 'weight', parseFloat(e.target.value) || 0)} /></td>
                  <td className="py-2 pr-3 w-20"><input className={inputCls} type="number" value={c.maxCapacity.volume} onChange={(e) => setNestedField('containers', i, 'maxCapacity', 'volume', parseFloat(e.target.value) || 0)} /></td>
                  <td className="py-2 pr-3 w-24"><input className={inputCls} type="number" value={c.cost} onChange={(e) => setField('containers', i, 'cost', parseFloat(e.target.value) || 0)} /></td>
                  <td className="py-2 pr-3"><Toggle value={c.refrigerated} onChange={(v) => setField('containers', i, 'refrigerated', v)} /></td>
                  <td className="py-2"><button className={btnDel} onClick={() => removeRow('containers', i)}><span className="material-symbols-outlined text-sm">delete</span></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pallets (conditional) */}
      {form.palletization && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-lg font-bold text-white">Pallet Types</h3>
            <button className={btnAdd} onClick={() => setForm((f) => ({ ...f, pallets: [...(f.pallets || []), { name: `Pallet ${(f.pallets || []).length + 1}`, length: 1.2, width: 1, height: 0.145, weight: 30, maxWeightAllowed: 1500, sameItemOnly: true, isDoubleStackingAllowed: false }] }))}>
              <span className="material-symbols-outlined text-base">add</span> Add Pallet
            </button>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full">
              <thead><tr>
                {['Name', 'Length (m)', 'Width (m)', 'Height (m)', 'Weight (kg)', 'Max Wt Allowed (kg)', 'Same Item Only', 'Double Stack', ''].map((h) => <th key={h} className={thCls}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(form.pallets || []).map((p, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-2 pr-3">
                      <AssetCombobox
                        value={p.name}
                        assetList={assets.pallet}
                        placeholder="Pallet name"
                        onChange={(v) => setField('pallets', i, 'name', v)}
                        onSelect={(a) => setForm((prev) => {
                          const arr = [...(prev.pallets || [])];
                          arr[i] = { ...arr[i], name: a.name, ...a.data };
                          return { ...prev, pallets: arr };
                        })}
                      />
                    </td>
                    {['length', 'width', 'height', 'weight', 'maxWeightAllowed'].map((f) => (
                      <td key={f} className="py-2 pr-3 w-20"><input className={inputCls} type="number" value={p[f]} onChange={(e) => setField('pallets', i, f, parseFloat(e.target.value) || 0)} /></td>
                    ))}
                    <td className="py-2 pr-3"><Toggle value={p.sameItemOnly} onChange={(v) => setField('pallets', i, 'sameItemOnly', v)} /></td>
                    <td className="py-2 pr-3"><Toggle value={p.isDoubleStackingAllowed} onChange={(v) => setField('pallets', i, 'isDoubleStackingAllowed', v)} /></td>
                    <td className="py-2"><button className={btnDel} onClick={() => removeRow('pallets', i)}><span className="material-symbols-outlined text-sm">delete</span></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Packages */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-lg font-bold text-white">Package Types</h3>
          <button className={btnAdd} onClick={() => setForm((f) => ({ ...f, packages: [...f.packages, { name: `Package ${f.packages.length + 1}`, length: 0, width: 0, height: 0, maxStackingWeight: 0, maxStackingCount: 1, orientationRestricted: false, refrigerated: false, quantity: 1 }] }))}>
            <span className="material-symbols-outlined text-base">add</span> Add Package
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full">
            <thead><tr>
              {['Name', 'Qty', 'Length (m)', 'Width (m)', 'Height (m)', 'Weight (kg)', 'Max Stack', 'Fixed Orient.', 'Refrig.', ''].map((h) => <th key={h} className={thCls}>{h}</th>)}
            </tr></thead>
            <tbody>
              {form.packages.map((pkg, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-2 pr-3">
                    <AssetCombobox
                      value={pkg.name}
                      assetList={assets.item}
                      placeholder="Package name"
                      onChange={(v) => setField('packages', i, 'name', v)}
                      onSelect={(a) => setForm((prev) => {
                        const arr = [...prev.packages];
                        arr[i] = { ...arr[i], name: a.name, ...a.data };
                        return { ...prev, packages: arr };
                      })}
                    />
                  </td>
                  <td className="py-2 pr-3 w-20"><input className={inputCls} type="number" value={pkg.quantity} onChange={(e) => setField('packages', i, 'quantity', parseInt(e.target.value) || 0)} /></td>
                  {['length', 'width', 'height', 'maxStackingWeight', 'maxStackingCount'].map((f) => (
                    <td key={f} className="py-2 pr-3 w-20"><input className={inputCls} type="number" value={pkg[f]} onChange={(e) => setField('packages', i, f, parseFloat(e.target.value) || 0)} /></td>
                  ))}
                  <td className="py-2 pr-3"><Toggle value={pkg.orientationRestricted} onChange={(v) => setField('packages', i, 'orientationRestricted', v)} /></td>
                  <td className="py-2 pr-3"><Toggle value={pkg.refrigerated} onChange={(v) => setField('packages', i, 'refrigerated', v)} /></td>
                  <td className="py-2"><button className={btnDel} onClick={() => removeRow('packages', i)}><span className="material-symbols-outlined text-sm">delete</span></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Run button */}
      <div className="flex justify-end">
        <button
          disabled={isLoading}
          onClick={handleRun}
          className="h-12 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-10 text-base font-bold text-white shadow-lg shadow-blue-600/30 transition hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Running…' : 'Run Simulation'}
        </button>
      </div>
    </section>
  );
};

export default SimulationForm;
